import {
  CapGainFilterType,
  CapGainListingRecordsCount,
  CapGainSelectedFilter,
} from '@products/types/tax.type';
import {
  Chip,
  DropdownComponent,
  ResponsiveService,
  SearchComponent,
} from '@frk/eds-components';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { TranslateService } from '@shared/translate/translate.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Logger } from '@utils/logger';

const logger = Logger.getLogger('USCapitalGainsFiltersComponent');
@Component({
  selector: 'ft-us-capital-gains-filters',
  templateUrl: './us-capital-gains-filters.component.html',
  styleUrls: ['./us-capital-gains-filters.component.scss'],
})
export class UsCapitalGainsFiltersComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() filters: CapGainFilterType[];
  @Input() hasNoShareClass: boolean;
  @Input() recordsCount: CapGainListingRecordsCount;
  @Input() isLoaded = false;
  @Output() selectedFilterType = new EventEmitter<string>();
  isHandheld$: Observable<boolean>;
  productTypeFilter: CapGainFilterType;
  otherFilterTypes: CapGainFilterType[];
  selectedFilters: CapGainSelectedFilter[] = [];
  isSearchActive = false;
  searchTerm: string;
  totalRecords: number;
  filteredRecords: number;
  debouncer$: Subject<string> = new Subject<string>();
  componentDestroyed$: Subject<boolean> = new Subject();

  @ViewChild('filterSearch') filterSearch: SearchComponent;
  @ViewChildren('filterDropdowns')
  filterDropdowns: QueryList<DropdownComponent>;

  constructor(
    protected responsiveService: ResponsiveService,
    private translateService: TranslateService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // ignore search term if new is called within 500ms
    // debouncer is triggered after search term
    this.debouncer$
      .pipe(takeUntil(this.componentDestroyed$), debounceTime(500))
      .subscribe((searchTerm: string) =>
        this.selectedFilterType.next(searchTerm)
      );

    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.totalRecords = this.recordsCount?.total;
    this.filteredRecords = this.recordsCount?.filtered;
    this.spinner.show('capitalGainsLoadingSpinner');
  }

  ngOnChanges(): void {
    this.totalRecords = this.recordsCount?.total;
    this.filteredRecords = this.recordsCount?.filtered;
    this.productTypeFilter = this.filters?.find(
      (filterType) => filterType.filterName === 'productType'
    );

    this.otherFilterTypes = this.filters?.filter(
      (filterType) => filterType.filterName !== 'productType'
    );

    // NGC-9140: Remove class fund filter when no row has the same.
    if (this.hasNoShareClass) {
      const shareClassIndex: number = this.otherFilterTypes.findIndex(
        (filterType) => filterType.filterName === 'shareClassName'
      );

      this.otherFilterTypes.splice(shareClassIndex, 1);
    }
    setTimeout(() => {
      this.isLoaded = true;
      this.cdr.detectChanges();
    }, 1000);
  }

  /**
   * Fetch selected filters for the chips.
   */
  fetchSelectedFilters(): void {
    this.selectedFilters = [];

    this.filters?.forEach((filter) => {
      const filterOptions: Chip[] = [];

      filter.filterOptions.forEach((filterOption) => {
        if (filterOption.checked) {
          filterOptions.push({
            label: this.translateService.instant(
              `products.capgains-filter-${filter.filterLabel}`
            ),
            value: filterOption.value,
          });
        }
      });

      if (filterOptions.length) {
        this.selectedFilters.push({
          filterType: filter.filterName,
          filterOptions,
        });
      }
    });
  }

  /**
   * Filter the cap gain listing based upon filter type & option(s) selected.
   * @param selectedFilterType filter type selected
   * @param selectedFilterTypeOption filter type option selected
   */
  filterListing(): void {
    this.isLoaded = false;
    this.fetchSelectedFilters();
    this.selectedFilterType.next(this.searchTerm);
  }

  /**
   * Filter the cap gain listing based upon search text entered.
   * @param searchText: searched string
   */
  searchByKeyword(searchText: string): void {
    this.isLoaded = false;
    searchText.trim() === ''
      ? (this.isSearchActive = false)
      : (this.isSearchActive = true);
    this.searchTerm = searchText;
    this.debouncer$.next(searchText);
    // this.selectedFilterType.next(this.searchTerm);
  }

  /**
   * Remove filter based on chip selected.
   * @param filterType filter type of the selected chip
   * @param selectedChip selected filter option for corresponding filter type
   */
  removeFilter(filterType: string, selectedChip: Chip): void {
    // 1. Find the 'filterType' & its selection and update its 'checked' status in state.
    this.filters.find((filter) => {
      if (filter.filterName === filterType) {
        filter.filterOptions.find((filterOption) => {
          if (filterOption.value === selectedChip.value) {
            filterOption.checked = false;
          }
        });
      }
    });

    // 2. Find the dropdown for corresponding 'filterType' & update its 'checked' status in its component.
    const targetFilterDropdown = this.filterDropdowns.find(
      (filterDropdown) => filterDropdown.id === filterType
    );
    targetFilterDropdown.getSelected({
      label: selectedChip.label,
      value: selectedChip.value,
      checked: false,
    });

    // 3. Update the listing based upon the filters.
    this.selectedFilterType.next(this.searchTerm);
  }

  /**
   * Reset the filters & search and hence reset the grid listing.
   * @param event event generated by link click.
   */
  resetFilters(event: PointerEvent): void {
    this.isLoaded = false;
    event.preventDefault();

    this.filters.forEach((filter) => {
      filter.filterOptions.forEach(
        (filterOption) => (filterOption.checked = false)
      );
    });
    this.filterDropdowns.forEach((dropdown) => dropdown.reset());
    this.selectedFilters = [];

    this.filterSearch.value = '';
    this.filterSearch.hasTyped = false;

    this.isSearchActive = false;

    this.searchTerm = '';

    this.selectedFilterType.next(this.searchTerm);
  }

  ngOnDestroy(): void {
    // unsubscribe from Observables
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
